import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, ButtonBase, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { Close, CloudUpload } from '@mui/icons-material';

interface FileDropzoneProps {
  title?: string;
  labels?: {
    default: string;
    dragActive: string;
  };
  imgUrl?: string;
  onSelectFile?: (file: File | null) => void;
  error?: string;
}

export const FileDropzone = ({
  title,
  labels = {
    default: 'Drag and drop an image file here or click',
    dragActive: 'Drop the file here',
  },
  imgUrl,
  onSelectFile = () => {},
  error,
}: FileDropzoneProps) => {
  const theme = useTheme();
  const onDrop = useCallback(([file]) => {
    if (file) {
      onSelectFile(file);
    }
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ multiple: false, maxFiles: 1, onDrop });

  const isError = !!error;

  const color = isDragActive
    ? theme.palette.primary.main
    : isError
    ? theme.palette.error.main
    : theme.palette.text.secondary;

  return (
    <>
      {title && (
        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
          <Typography variant="subtitle1" sx={{ fontSize: 12, color: theme.palette.text.secondary }}>
            {title}
          </Typography>
        </Box>
      )}

      <Box sx={{ position: 'relative' }} {...getRootProps()}>
        <input {...getInputProps()} />

        <Tooltip title={imgUrl ? `Drag 'n' drop a file or click to change` : ''} arrow placement="top">
          <ButtonBase
            sx={{
              width: '100%',
              border: 1,
              borderStyle: 'dashed',
              borderRadius: 1,
              borderColor: color,
            }}
          >
            {imgUrl ? (
              <Box sx={{ height: 120 }}>
                <img
                  src={imgUrl}
                  alt="Attatched file"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  color,
                  height: 120,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-around',
                  alignItems: 'center',
                  paddingY: 2.5,
                  paddingX: 2,
                }}
              >
                <Typography sx={{ color: 'inherit' }}>{isDragActive ? labels.dragActive : labels.default}</Typography>

                <CloudUpload sx={{ color: 'inherit', fontSize: 32, marginTop: 1 }} />
              </Box>
            )}
          </ButtonBase>
        </Tooltip>

        {imgUrl && (
          <Box
            sx={{
              position: 'absolute',
              top: -14,
              right: -14,
              bgcolor: theme.palette.primary.main,
              borderRadius: '100%',
            }}
          >
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onSelectFile(null);
              }}
            >
              <Close sx={{ color: theme.palette.common.white, fontSize: 'inherit' }} />
            </IconButton>
          </Box>
        )}
      </Box>

      {isError && (
        <Typography variant="caption" color={theme.palette.error.main} sx={{ marginLeft: '14px' }}>
          {error}
        </Typography>
      )}
    </>
  );
};
